import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQueryClient } from '@tanstack/react-query';
import { profileKeys, useProfileQuery } from '@entities/Profile';
import { useAuthStore } from '@entities/User';
import { EmailText, resendLabelKey, useResendCooldown } from '@features/EmailVerify';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { useNetworkStore } from '@shared/network';
import type { EmailConfirmError } from '../api/emailConfirmApi';
import { confirmScreen, type ConfirmScreen } from '../model/confirmState';
import { useVerifyEmailQuery } from '../model/queries';

const REDIRECT_SECONDS = 5;

const columnClass = 'flex w-full max-w-[400px] flex-col items-center gap-[18px] text-center';
// cn — только clsx (без tailwind-merge): размеры заголовков — отдельными строками, без конфликта классов.
const titleBase = 'font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em]';
const titleClass = `${titleBase} sm:text-[36px]`;
const successTitleClass = `${titleBase} sm:text-[38px]`;
const textClass = 'text-[14px] leading-[1.6] text-muted';
// Основная кнопка: на телефоне — во всю ширину колонки, шире — по содержимому; подпись в одну строку.
const primaryBase =
  'inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-pill px-7 py-4 text-[15px] font-medium tracking-[0.02em] transition-colors sm:w-auto';
const primaryClass = cn(primaryBase, 'bg-accent text-fg-inv hover:bg-accent-hi');
// Неактивная (отсчёт до повтора) — aria-disabled, а не disabled: фокус остаётся на кнопке.
const primaryIdleClass = cn(primaryBase, 'cursor-not-allowed bg-elev text-muted');
// Вторая — текстовой ссылкой под основной, область нажатия 44 px.
const secondaryClass =
  'inline-flex min-h-[44px] items-center justify-center px-3 text-[14px] font-medium text-accent underline-offset-4 hover:underline';

const transition = { duration: 0.32 };
const enter = { opacity: 0, y: 8 };
const shown = { opacity: 1, y: 0 };
const leave = { opacity: 0, y: -8 };

type Glyph = 'success' | 'mail' | 'error';

function StateGlyph({ kind }: { kind: Glyph }) {
  return (
    <motion.div
      initial={{ scale: 0.4, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, type: 'spring', stiffness: 240, damping: kind === 'success' ? 14 : 16 }}
      className={cn(
        'inline-flex h-16 w-16 items-center justify-center rounded-[20px] text-3xl',
        kind === 'success' && 'bg-success/15 text-success',
        kind === 'mail' && 'bg-warn/15 text-warn',
        kind === 'error' && 'bg-danger/15 text-danger',
      )}
      aria-hidden
    >
      {kind === 'success' ? '✓' : kind === 'mail' ? '✉' : '✕'}
    </motion.div>
  );
}

/** Адрес — ПДн: amp-mask / ym-hide-content прячут его в записях сессий; перенос после «@». */
function Email({ email }: { email: string }) {
  return (
    <span className="amp-mask ym-hide-content text-fg [overflow-wrap:anywhere]">
      <EmailText email={email} />
    </span>
  );
}

/**
 * Страница ссылки из письма подтверждения (B6, ТЗ демо 1.2). Устаревшая ссылка (400 TOKEN_EXPIRED:
 * истекла, уже использована или неверна — сервер не различает) у вошедшего с подтверждённой почтой —
 * «Почта уже подтверждена», у вошедшего без неё — новое письмо отсюда же, у гостя — «Войти».
 */
export function EmailConfirmContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const stage = useAuthStore((s) => s.stage);
  const storedUser = useAuthStore((s) => s.user);
  const signedIn = stage === 'app';

  const [initialToken] = useState(() => searchParams.get('token'));
  const [redirectIn, setRedirectIn] = useState(REDIRECT_SECONDS);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [sent, setSent] = useState(false);

  const verify = useVerifyEmailQuery(initialToken);
  const errorCode = (verify.error as EmailConfirmError | null)?.code ?? null;
  const expired = verify.isError && errorCode === 'EXPIRED_TOKEN';
  // Подтверждена ли почта — по свежему профилю: стор мог устареть (подтвердил на телефоне, ссылку
  // открыл на компьютере). Профиль не загрузился — по стору.
  const profileQuery = useProfileQuery({ enabled: signedIn && expired });
  const emailVerified = !signedIn
    ? false
    : profileQuery.data
      ? profileQuery.data.user.is_email_verified
      : profileQuery.isError
        ? (storedUser?.emailVerified ?? false)
        : undefined;
  const email = profileQuery.data?.user.email ?? storedUser?.email ?? '';

  const screen: ConfirmScreen = confirmScreen({
    hasToken: !!initialToken,
    verifying: verify.isFetching,
    verified: verify.isSuccess,
    error: verify.isError ? errorCode : null,
    signedIn,
    emailVerified,
    alreadyVerified,
  });

  // «Нет сети» экран говорит сам и даёт свою «Повторить» — общая плашка сети в это время не показывается: два
  // сообщения и две «Повторить» об одном, а на телефоне плашка закрывала шапку (R2-03, как в тренировке — ТЗ демо
  // 1.5). Пока идёт сама проверка ссылки (и повтор), плашка тоже ждёт: её исход скажет экран, без вспышки плашки.
  const setBannerSuppressed = useNetworkStore((s) => s.setBannerSuppressed);
  const suppressBanner = screen === 'network' || (screen === 'pending' && verify.isFetching);
  useLayoutEffect(() => {
    if (!suppressBanner) return;
    setBannerSuppressed(true);
    return () => {
      setBannerSuppressed(false);
      // Экран уже сменился на итог — «Связь восстановлена» после него не нужна.
      const network = useNetworkStore.getState();
      if (network.status === 'reconnected') network.setStatus('online');
    };
  }, [suppressBanner, setBannerSuppressed]);

  const resend = useResendCooldown({
    onAlreadyVerified: () => {
      setAlreadyVerified(true);
      void qc.invalidateQueries({ queryKey: profileKeys.current });
    },
  });

  useEffect(() => {
    if (initialToken && searchParams.has('token')) {
      setSearchParams({}, { replace: true });
    }
  }, [initialToken, searchParams, setSearchParams]);

  // Почта подтверждена — профиль перечитается, и главная увидит это без перезагрузки.
  useEffect(() => {
    if (verify.isSuccess && signedIn) void qc.invalidateQueries({ queryKey: profileKeys.current });
  }, [verify.isSuccess, signedIn, qc]);

  useEffect(() => {
    if (resend.state === 'sent') setSent(true);
  }, [resend.state]);

  const redirecting = screen === 'success' || screen === 'already';
  const redirectTo = signedIn ? paths.dashboard : paths.auth;

  useEffect(() => {
    if (!redirecting) return;
    if (redirectIn <= 0) {
      navigate(redirectTo, { replace: true });
      return;
    }
    const id = window.setTimeout(() => setRedirectIn((n) => n - 1), 1000);
    return () => window.clearTimeout(id);
  }, [redirecting, redirectIn, navigate, redirectTo]);

  const retry = () => {
    if (!verify.isFetching) void verify.refetch();
  };
  const backLink = (asText: boolean) => (
    <Link
      to={signedIn ? paths.dashboard : paths.auth}
      className={
        asText
          ? secondaryClass
          : 'mt-1 inline-flex items-center justify-center gap-2 rounded-pill border border-line bg-transparent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg transition-colors hover:bg-elev'
      }
    >
      {t(signedIn ? 'auth.confirmEmail.success.goApp' : 'auth.confirmEmail.error.back')}
    </Link>
  );

  const redirectNote = (
    <div className="mt-1.5 flex w-full items-center gap-3.5 rounded-2xl border border-line bg-card px-4 py-3.5 text-left">
      <span
        aria-hidden
        className="block h-8 w-8 flex-shrink-0 animate-spin rounded-full border-2 border-line border-t-accent"
      />
      <div className="flex-1 text-[14px] leading-[1.5] text-muted">
        {t('auth.confirmEmail.success.redirectPrefix')}{' '}
        <b className="font-medium text-fg">
          {t(signedIn ? 'auth.confirmEmail.success.targetApp' : 'auth.confirmEmail.success.targetSignIn')}
        </b>
        {t('auth.confirmEmail.success.redirectSuffix')}
      </div>
      <motion.div
        key={redirectIn}
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.25, ease: 'backOut' }}
        className="rounded-lg bg-accent/10 px-2.5 py-[5px] font-mono text-[14px] font-medium tracking-[0.04em] text-accent"
      >
        {Math.max(0, redirectIn)}
      </motion.div>
    </div>
  );

  let body: ReactNode;
  switch (screen) {
    case 'pending':
      body = (
        <>
          <span
            aria-hidden
            className="block h-12 w-12 animate-spin rounded-full border-2 border-line border-t-accent"
          />
          <h1 className={titleClass}>{t('auth.confirmEmail.pending.title')}</h1>
          <div className={textClass}>{t('auth.confirmEmail.pending.sub')}</div>
        </>
      );
      break;
    case 'success':
    case 'already':
      body = (
        <>
          <StateGlyph kind="success" />
          {screen === 'success' ? (
            <h1 className={successTitleClass}>
              {t('auth.confirmEmail.success.title')}{' '}
              <em className="font-normal italic text-accent">{t('auth.confirmEmail.success.titleEm')}</em>.
            </h1>
          ) : (
            <h1 className={titleClass}>{t('auth.confirmEmail.already.title')}</h1>
          )}
          <div className={textClass}>
            {t(screen === 'success' ? 'auth.confirmEmail.success.sub' : 'auth.confirmEmail.already.sub')}
          </div>
          {redirectNote}
          <Link to={redirectTo} className={primaryClass}>
            <span>{t(signedIn ? 'auth.confirmEmail.success.goApp' : 'auth.confirmEmail.success.goSignIn')}</span>
            <span aria-hidden className="text-sm">
              →
            </span>
          </Link>
        </>
      );
      break;
    case 'expiredApp':
      body = (
        <>
          <StateGlyph kind="mail" />
          <h1 className={titleClass}>{t('auth.confirmEmail.error.expiredTitle')}</h1>
          {/* role=status: после «Отправить новое письмо» текст меняется — диктор его прочтёт. */}
          <div className={textClass} role="status">
            {sent ? (
              <>
                {t('auth.confirmEmail.error.sentPrefix')} <Email email={email} />
                {t('auth.confirmEmail.error.sentSuffix')}
              </>
            ) : (
              <>
                {t('auth.confirmEmail.error.expiredAppPrefix')} <Email email={email} />
              </>
            )}
          </div>
          <div className="flex w-full flex-col items-center gap-1">
            <button
              type="button"
              className={resend.disabled ? primaryIdleClass : primaryClass}
              onClick={() => void resend.resend()}
              aria-disabled={resend.disabled || undefined}
            >
              {/* «Отправлено» уже говорит текст над кнопкой — после отправки сразу отсчёт (ТЗ демо 1.2). */}
              {resend.state === 'idle' && !sent
                ? t('auth.confirmEmail.error.resend')
                : t(`emailVerify.full.${resendLabelKey[resend.state === 'sent' ? 'cooldown' : resend.state]}`, {
                    n: resend.cooldown,
                  })}
            </button>
            {backLink(true)}
          </div>
        </>
      );
      break;
    case 'expiredGuest':
      body = (
        <>
          <StateGlyph kind="mail" />
          <h1 className={titleClass}>{t('auth.confirmEmail.error.expiredTitle')}</h1>
          <div className={textClass}>{t('auth.confirmEmail.error.expiredGuest')}</div>
          <Link to={paths.auth} className={primaryClass}>
            {t('auth.confirmEmail.error.signIn')}
          </Link>
        </>
      );
      break;
    case 'missing':
      body = (
        <>
          <StateGlyph kind="error" />
          <h1 className={titleClass}>{t('auth.confirmEmail.error.title')}</h1>
          <div className={textClass}>{t('auth.confirmEmail.error.missing')}</div>
          {backLink(false)}
        </>
      );
      break;
    default:
      // Нет сети, 429, сбой сервера — тот же запрос с той же ссылкой.
      body = (
        <>
          <StateGlyph kind="error" />
          <h1 className={titleClass}>{t('auth.confirmEmail.error.title')}</h1>
          <div className={textClass}>
            {t(
              screen === 'network'
                ? 'auth.confirmEmail.error.network'
                : screen === 'rateLimit'
                  ? 'auth.confirmEmail.error.rateLimit'
                  : 'auth.confirmEmail.error.generic',
            )}
          </div>
          <div className="flex w-full flex-col items-center gap-1">
            <button type="button" className={primaryClass} onClick={retry}>
              {t('auth.confirmEmail.error.retry')}
            </button>
            {backLink(true)}
          </div>
        </>
      );
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div key={screen} initial={enter} animate={shown} exit={leave} transition={transition} className={columnClass}>
        {body}
      </motion.div>
    </AnimatePresence>
  );
}
